import React, { useEffect, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Image from 'next/image';
import cn from 'classnames';

import SmallSearchInput from 'react-scale/components/pages/open-datasets/SmallSearchInput';
import RangeSlider from 'react-scale/components/pages/open-datasets/RangeSlider';
import sectionTitleIcons from 'react-scale/components/new-global/icons/SectionTitleIcons';
import { Button, Section, SectionTitle, Tooltip } from 'react-scale/components';
import Filters, {
  FilterBox,
  FilterName,
  FilterItem
} from 'react-scale/components/elements/filters';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import styles from './OpenDatasetsList.module.css';
import Loader from './loader/index.tsx';
import DatasetAccordion from './dataset-accordion';

const OpenDatasetsList = () => {
  const router = useRouter();
  const [show, setShow] = useState(10);
  const [search, setSearch] = useState('');
  const { s, ...restQuery } = router.query;
  const { data, isValidating } = useSWR(
    ['/api/content/open-dataset', restQuery, s],
    ([url]) => fetch(url + window.location.search).then(res => res.json()),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false
    }
  );

  useEffect(() => {
    const uri = new URL(window.location.href);
    const searchValue = uri.searchParams.get('s');
    if (typeof searchValue === 'string') {
      setSearch(searchValue);
    }
  }, []);

  const handleSearch = event => {
    setSearch(event.target.value);
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...restQuery,
          ...(event.target.value ? { s: event.target.value } : {})
        }
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };
  const handleClearSearch = () => {
    setSearch('');
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...restQuery,
          ...{}
        }
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const handleAddFilter = (type, value) => {
    const query = { ...router.query };
    let newQueryType = !query[type]
      ? value
      : query[type]?.includes(value)
      ? query[type].replace(query[type].split(',')[0] === value ? value : `, ${value}`, '')
      : `${query[type]}, ${value}`;

    delete query[type];

    if (newQueryType.startsWith(', ')) newQueryType = newQueryType.substring(2);

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...query,
          ...(newQueryType ? { [type]: newQueryType } : {})
        }
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const updateYearRange = yearRange => {
    const query = { ...router.query };

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...query,
          ...(yearRange ? { minYear: yearRange.min, maxYear: yearRange.max } : {})
        }
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const clearFilters = () => {
    handleClearSearch();
    router.replace(router.basePath, undefined, { scroll: false });
  };

  const showMore = () =>
    setShow(prevState => (prevState >= data?.lenght ? prevState : prevState + 10));

  return (
    <Section bg="bg-black">
      <div className="max-w-2xl">
        <SectionTitle
          overtitle="Favorites"
          title="List of Open&#45;Source Datasets"
          icon={sectionTitleIcons.openDatasets}
          subtitle="There are public datasets about nearly any subject. The Scale AI team have indexed a list of our favorites."
        />
      </div>
      <div>
        <div id="datasets-table" className="mb-8 text-right">
          <span className="hidden leading-8 text-white lg:block">
            {`Showing ${data?.length ?? 0} result${data?.length === 1 ? '' : 's'}`}
          </span>
          <span className="leading-8 text-white lg:hidden">
            {`Showing ${show <= data?.length ? show : data?.length ?? 0} of ${
              data?.length ?? 0
            } result${data?.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className={`${styles.datasetsList} lg:grid gap-8`}>
          <aside>
            <StickyBox offsetTop={20} offsetBottom={20}>
              <SmallSearchInput
                className="mb-8"
                searchValue={search}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                onBlur={handleSearch}
              />

              <Filters initialValues={{ data: [], diversity: [], annotation: [] }}>
                {() => (
                  <FilterBox bg="bg-tundora" resetFilters={clearFilters}>
                    <div className="mb-12 lg:mb-8">
                      <FilterName label="Data Provided" />
                      <div className="space-y-4 lg:space-y-[6px]">
                        {['Vehicle Data', 'Video', 'Image', 'GPS', 'Codes', 'Thermal', 'LiDAR'].map(
                          item => (
                            <FilterItem
                              key={item}
                              type="data"
                              label={item}
                              value={item}
                              onClick={() => handleAddFilter('data', item)}
                              checked={router.query.data?.includes(item)}
                            />
                          )
                        )}
                      </div>
                    </div>
                    <div className="space-y-[72px] lg:space-y-14 mb-14 lg:mb-10">
                      <div>
                        <FilterName label="Date" />
                      </div>
                      <RangeSlider onYearUpdate={yearRange => updateYearRange(yearRange)} />
                    </div>
                    <div className="mb-12 lg:mb-8">
                      <FilterName label="Diversity" />
                      <div className="space-y-4 lg:space-y-[6px]">
                        {['Weather', 'Season', 'Night', 'Illumination'].map(item => (
                          <FilterItem
                            key={item}
                            type="diversity"
                            label={item}
                            value={item}
                            onClick={() => handleAddFilter('diversity', item)}
                            checked={router.query.diversity?.includes(item)}
                          />
                        ))}
                      </div>
                    </div>
                    <FilterName label="Annotation" />
                    <div className="space-y-4 lg:space-y-[6px]">
                      {[
                        'Behavioral Label',
                        'Bounding Box',
                        'Lane Marking',
                        'Optical Flow',
                        'Semantic Label',
                        'Stixel'
                      ].map(item => (
                        <FilterItem
                          key={item}
                          type="annotation"
                          label={item}
                          value={item}
                          onClick={() => handleAddFilter('annotation', item)}
                          checked={router.query.annotation?.includes(item)}
                        />
                      ))}
                    </div>
                  </FilterBox>
                )}
              </Filters>
            </StickyBox>
          </aside>

          <DatasetAccordion datasets={data} show={show} loading={isValidating} />
          {show < data?.length && (
            <div className={styles.cta}>
              <Button text="See More" onClick={showMore} />
            </div>
          )}

          <div className="relative hidden max-w-full overflow-scroll lg:overflow-auto lg:block">
            <table className={`${styles.table} w-full text-left`}>
              <thead>
                <tr>
                  <th className={styles.dataset}>
                    <span className="text-xs font-normal leading-6 text-neutral-300">Dataset</span>
                  </th>
                  <th className={styles.dataProvided}>
                    <span className="text-xs font-normal leading-6 text-neutral-300 whitespace-nowrap">
                      Data Provided
                    </span>
                  </th>
                  <th className={styles.annotation}>
                    <span className="text-xs font-normal leading-6 text-neutral-300">
                      Annotation
                    </span>
                  </th>
                  <th>
                    <span className="text-xs font-normal leading-6 text-neutral-300">
                      Diversity
                    </span>
                  </th>
                  <th>
                    <span className="text-xs font-normal leading-6 text-neutral-300">Traffic</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isValidating ? (
                  <Loader length={7} />
                ) : data?.length ?? -1 > 0 ? (
                  data.map(listItem => <ListItem key={listItem.title} listItem={listItem} />)
                ) : (
                  <td className="text-neutral-300 text-center !py-24" colSpan="5">
                    There are no results for the filters provided
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="block mx-auto mt-1 text-white cursor-pointer max-w-max"
                    >
                      Clear Filters
                    </button>
                  </td>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Section>
  );
};

const ListItem = ({ listItem }) => (
  <a
    className="table-row transition-colors duration-300 hover:bg-neutral-900"
    href={listItem.link ?? '#'}
    target="_blank"
    rel="noreferrer"
  >
    <td className="align-middle dataset">
      <div className="flex items-center">
        <div
          className={cn('w-[68px] h-[68px] relative overflow-hidden rounded-full', {
            'bg-neutral-300': !listItem.logoBack.url
          })}
        >
          {listItem.logoBack.url && (
            <Image
              src={listItem.logoBack.url}
              alt={listItem.logoBack.alt}
              layout="fill"
              objectFit="cover"
            />
          )}
          <Image
            src={listItem.logoFront.url}
            alt={listItem.logoFront.alt}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <span className="flex-1 ml-4">
          <p className="text-xs leading-5 text-white">
            {listItem.title} ({listItem.time ?? listItem.date.slice(0, 4)})
          </p>
          <p className="text-xs leading-5 text-neutral-500">{`by ${listItem.provider}`}</p>
          <p className="text-xs leading-5 text-white">{listItem.location}</p>
        </span>
      </div>
    </td>
    <td className="align-middle data-provided">
      <Tooltip
        content={
          <div className="bg-neutral-800 rounded-[4px] p-4 text-white">{listItem.dataProvided}</div>
        }
        arrow={{
          align: 'center',
          width: 14,
          height: 8,
          className: 'fill-current text-neutral-800'
        }}
      >
        <p className="overflow-hidden text-xs leading-5 text-white max-w-max whitespace-nowrap text-ellipsis">
          {listItem.dataProvided}
        </p>
      </Tooltip>
    </td>
    <td className="align-middle annotation">
      <Tooltip
        content={
          <div className="bg-neutral-800 rounded-[4px] p-4 text-white">{listItem.annotation}</div>
        }
        arrow={{
          align: 'center',
          width: 14,
          height: 8,
          className: 'fill-current text-neutral-800'
        }}
      >
        <p className="overflow-hidden text-xs leading-5 text-white max-w-max whitespace-nowrap text-ellipsis">
          {listItem.annotation}
        </p>
      </Tooltip>
    </td>
    <td className="align-middle">
      <Tooltip
        content={
          <div className="bg-neutral-800 rounded-[4px] p-4 text-white">{listItem.diversity}</div>
        }
        arrow={{
          align: 'center',
          width: 14,
          height: 8,
          className: 'fill-current text-neutral-800'
        }}
      >
        <p className="overflow-hidden text-xs leading-5 text-white whitespace-nowrap text-ellipsis">
          {listItem.diversity}
        </p>
      </Tooltip>
    </td>
    <td className="align-middle">
      <Tooltip
        content={
          <div className="bg-neutral-800 rounded-[4px] p-4 text-white">{listItem.traffic}</div>
        }
        arrow={{
          align: 'center',
          width: 14,
          height: 8,
          className: 'fill-current text-neutral-800'
        }}
      >
        <p className="overflow-hidden text-xs leading-5 text-white whitespace-nowrap text-ellipsis">
          {listItem.traffic}
        </p>
      </Tooltip>
    </td>
  </a>
);

export default React.memo(OpenDatasetsList);
